// client/src/pages/ServicesTester.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';

// Common layout
import PageShell from '../components/common/PageShell';

// Constants
import { DIRECT_SERVICES } from '../components/services_tester/common/constants.js';

// Thunks
import {
  performQuickHealthCheck,
  sendRestRequest,
  checkInfrastructureAPI,
  checkRabbitMQHealth,
  checkRabbitMQDetailed,
  checkTarantoolHealth,
  checkTarantoolDetailed,
  checkMongoDBHealth,
  checkMongoDBDetailed,
  checkClickHouseHealth,
  checkClickHouseDetailed,
  testRabbitMQPublish,
  checkFeatureTreeGeneration,
  checkKratosHealth,
  checkKratosAdminHealth,
  checkKratosUsers,
  performCompleteHealthCheck,
  checkKratosDetailed,
} from '../store/thunks/directServicesThunks';

// Slices
import {
  setActiveTab,
  setSelectedService,
  setRestPath,
  setRestMethod,
  setRestBody,
  clearLogs,
  addLog,
} from '../store/slices/servicesTesterSlice';

// Components
import ServicesTesterHeader from '../components/services_tester/ServicesTesterHeader';
import ServicesTesterActions from '../components/services_tester/ServicesTesterActions';
import ServicesTesterContent from '../components/services_tester/ServicesTesterContent';

// API Tester
import apiTester from '../api/apiTester';
import Gateway from '../api/gateway';

// Modals
import ServiceTestModal from '../components/services_tester/ServiceTestModal.jsx';
import RoutesModal from '../components/services_tester/RoutesModal.jsx';
import RabbitMQDetailsModal from '../components/services_tester/RabbitMQDetailsModal.jsx';
import KratosDetailsModal from '../components/services_tester/KratosDetailsModal.jsx';

export default function ServicesTester() {
  const dispatch = useDispatch();
  const state = useSelector((state) => state.servicesTester);
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const {
    activeTab,
    healthCheckLoading,
    logs,
    networkDiagnostics,
    rabbitmqStatus,
    tarantoolStatus,
    mongodbStatus,
    clickhouseStatus,
    featureTreeStatus,
    selectedService,
    restPath,
    restMethod,
    restBody,
    restResponse,
    restLoading,
    infrastructure,
  } = state;

  // Modal states
  const [serviceTestModal, setServiceTestModal] = useState({ open: false, service: null });
  const [routesModal, setRoutesModal] = useState(false);
  const [rabbitmqDetailsModal, setRabbitmqDetailsModal] = useState(false);
  const [kratosDetailsModal, setKratosDetailsModal] = useState(false);

  // Feature states
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [infrastructureLoading, setInfrastructureLoading] = useState({
    rabbitmq: false,
    tarantool: false,
    mongodb: false,
    clickhouse: false,
    kratos: false,
    kratos_admin: false,
    featureTree: false,
  });

  // Kratos detailed
  const [kratosDetailed, setKratosDetailed] = useState(null);

  // Auth states
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Logs states
  const [autoScroll, setAutoScroll] = useState(true);
  const [logFilter, setLogFilter] = useState('all');
  const [logExpanded, setLogExpanded] = useState(false);
  const [logSearch, setLogSearch] = useState('');
  const [logLevels, setLogLevels] = useState({
    error: true,
    warning: true,
    success: true,
    info: true,
    debug: true,
  });
  const logAreaRef = useRef(null);

  // ImageLang states
  const [imageLangEncodeResult, setImageLangEncodeResult] = useState(null);
  const [imageLangDecodeResult, setImageLangDecodeResult] = useState(null);

  const kratosStatus = useMemo(
    () => ({
      public: infrastructure?.kratos?.public || null,
      admin: infrastructure?.kratos?.admin || null,
      users: infrastructure?.kratos?.users || null,
      detailed: kratosDetailed,
    }),
    [infrastructure, kratosDetailed]
  );

  useEffect(() => {
    apiTester.setLogCallback((message, type, data) => {
      console.log(`[API-TESTER:${type.toUpperCase()}] ${message}`, data || '');
    });
  }, []);

  useEffect(() => {
    dispatch(performQuickHealthCheck());
    dispatch(checkInfrastructureAPI());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageLangEncode = useCallback(
    async (features) => {
      if (!features) {
        dispatch(addLog({ msg: '❌ Encode: features пустые', type: 'error' }));
        return;
      }
      try {
        dispatch(addLog({ msg: '🖼️ ImageLang encode…', type: 'info' }));
        const res = await Gateway.http.imcon.post('/api/image-lang/encode', { features });
        const data = res?.data;
        if (data?.success) {
          setImageLangEncodeResult(data);
          dispatch(
            addLog({
              msg: `✅ Образ закодирован: bit_id=${data.bit_id}`,
              type: 'success',
              data,
            })
          );
        } else {
          dispatch(
            addLog({
              msg: `❌ Ошибка кодирования: ${data?.error || 'unknown'}`,
              type: 'error',
            })
          );
        }
      } catch (err) {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message;
        dispatch(addLog({ msg: `❌ Ошибка кодирования: ${msg}`, type: 'error' }));
      }
    },
    [dispatch]
  );

  const handleImageLangDecode = useCallback(
    async (bitId) => {
      if (!bitId) {
        dispatch(addLog({ msg: '❌ Decode: bit_id пустой', type: 'error' }));
        return;
      }
      try {
        dispatch(addLog({ msg: `🖼️ ImageLang decode ${bitId}…`, type: 'info' }));
        const res = await Gateway.http.imcon.post('/api/image-lang/decode', {
          bit_id: bitId,
        });
        const data = res?.data;
        if (data?.success) {
          setImageLangDecodeResult(data);
          dispatch(
            addLog({
              msg: `✅ Образ декодирован (bit_id=${data.bit_id})`,
              type: 'success',
              data,
            })
          );
        } else {
          dispatch(
            addLog({
              msg: `❌ Ошибка декодирования: ${data?.error || 'unknown'}`,
              type: 'error',
            })
          );
        }
      } catch (err) {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message;
        dispatch(addLog({ msg: `❌ Ошибка декодирования: ${msg}`, type: 'error' }));
      }
    },
    [dispatch]
  );

  const handleLogin = useCallback(async () => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const result = await apiTester.kratosLogin();
      if (result.success) {
        dispatch(
          addLog({
            msg: '✅ Вход выполнен успешно',
            type: 'success',
            data: { user: result.user },
          })
        );
        window.location.reload();
      } else {
        setAuthError(result.error || 'Ошибка входа');
        dispatch(addLog({ msg: `❌ Ошибка входа: ${result.error}`, type: 'error' }));
      }
    } catch (error) {
      setAuthError(error.message);
      dispatch(addLog({ msg: `❌ Ошибка входа: ${error.message}`, type: 'error' }));
    } finally {
      setAuthLoading(false);
    }
  }, [dispatch]);

  const handleLogout = useCallback(async () => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const result = await apiTester.kratosLogout();
      if (result.success) {
        dispatch(addLog({ msg: '✅ Выход выполнен успешно', type: 'success' }));
        window.location.reload();
      } else {
        setAuthError(result.error || 'Ошибка выхода');
        dispatch(addLog({ msg: `❌ Ошибка выхода: ${result.error}`, type: 'error' }));
      }
    } catch (error) {
      setAuthError(error.message);
      dispatch(addLog({ msg: `❌ Ошибка выхода: ${error.message}`, type: 'error' }));
    } finally {
      setAuthLoading(false);
    }
  }, [dispatch]);

  const handleCheckAuth = useCallback(async () => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const result = await apiTester.checkKratosSession();
      dispatch(
        addLog({
          msg: `🔐 Проверка сессии: ${result.success ? 'активна' : 'неактивна'}`,
          type: result.success ? 'success' : 'warning',
          data: result,
        })
      );
    } catch (error) {
      setAuthError(error.message);
      dispatch(addLog({ msg: `❌ Ошибка проверки сессии: ${error.message}`, type: 'error' }));
    } finally {
      setAuthLoading(false);
    }
  }, [dispatch]);

  const handleQuickHealthCheck = useCallback(() => {
    dispatch(performQuickHealthCheck());
  }, [dispatch]);

  const handleCompleteHealthCheck = useCallback(() => {
    dispatch(performCompleteHealthCheck());
  }, [dispatch]);

  const handleInfrastructureCheck = useCallback(async () => {
    setInfrastructureLoading({
      rabbitmq: true,
      tarantool: true,
      mongodb: true,
      clickhouse: true,
      kratos: true,
      kratos_admin: true,
      featureTree: true,
    });
    try {
      await dispatch(checkInfrastructureAPI());
    } finally {
      setInfrastructureLoading({
        rabbitmq: false,
        tarantool: false,
        mongodb: false,
        clickhouse: false,
        kratos: false,
        kratos_admin: false,
        featureTree: false,
      });
    }
  }, [dispatch]);

  const handleServiceTest = useCallback((service) => {
    setServiceTestModal({ open: true, service });
  }, []);

  const handleSendCustomRequest = useCallback(
    (serviceKey, path, method, body) => {
      const service = DIRECT_SERVICES.find((s) => s.key === serviceKey);
      if (!service) return;

      const fullUrl = `${service.url}${path}`;

      dispatch(
        sendRestRequest({
          url: fullUrl,
          method,
          body,
          serviceKey,
        })
      );
    },
    [dispatch]
  );

  const handleSendRestRequest = useCallback(() => {
    if (!selectedService || !restPath) {
      dispatch(addLog({ msg: '❌ Выберите сервис и укажите путь', type: 'error' }));
      return;
    }

    const service = DIRECT_SERVICES.find((s) => s.key === selectedService);
    if (!service) return;

    const fullUrl = `${service.url}${restPath}`;

    dispatch(
      sendRestRequest({
        url: fullUrl,
        method: restMethod,
        body: restBody,
        serviceKey: selectedService,
      })
    );
  }, [dispatch, selectedService, restPath, restMethod, restBody]);

  const handleClearLogs = useCallback(() => {
    dispatch(clearLogs());
  }, [dispatch]);

  const handleExportLogs = useCallback(() => {
    if (logs.length === 0) return;

    try {
      const logData = JSON.stringify(logs, null, 2);
      const blob = new Blob([logData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `service-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      dispatch(addLog({ msg: '📁 Логи экспортированы', type: 'success' }));
    } catch (error) {
      dispatch(addLog({ msg: `❌ Ошибка экспорта логов: ${error.message}`, type: 'error' }));
    }
  }, [logs, dispatch]);

  const scrollToTop = useCallback(() => {
    if (logAreaRef.current) {
      logAreaRef.current.scrollTop = 0;
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (logAreaRef.current) {
      logAreaRef.current.scrollTop = logAreaRef.current.scrollHeight;
    }
  }, []);

  const handleRabbitMQHealth = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, rabbitmq: true }));
    try {
      await dispatch(checkRabbitMQHealth());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, rabbitmq: false }));
    }
  }, [dispatch]);

  const handleRabbitMQDetails = useCallback(() => {
    setRabbitmqDetailsModal(true);
  }, []);

  const handleRefreshRabbitMQDetails = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, rabbitmq: true }));
    try {
      await dispatch(checkRabbitMQDetailed());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, rabbitmq: false }));
    }
  }, [dispatch]);

  const handleRabbitMQTest = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, rabbitmq: true }));
    try {
      await dispatch(testRabbitMQPublish());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, rabbitmq: false }));
    }
  }, [dispatch]);

  const handleTarantoolHealth = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, tarantool: true }));
    try {
      await dispatch(checkTarantoolHealth());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, tarantool: false }));
    }
  }, [dispatch]);

  const handleMongoDBHealth = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, mongodb: true }));
    try {
      await dispatch(checkMongoDBHealth());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, mongodb: false }));
    }
  }, [dispatch]);

  const handleClickHouseHealth = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, clickhouse: true }));
    try {
      await dispatch(checkClickHouseHealth());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, clickhouse: false }));
    }
  }, [dispatch]);

  const handleTarantoolDetailed = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, tarantool: true }));
    try {
      await dispatch(checkTarantoolDetailed());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, tarantool: false }));
    }
  }, [dispatch]);

  const handleMongoDBDetailed = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, mongodb: true }));
    try {
      await dispatch(checkMongoDBDetailed());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, mongodb: false }));
    }
  }, [dispatch]);

  const handleClickHouseDetailed = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, clickhouse: true }));
    try {
      await dispatch(checkClickHouseDetailed());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, clickhouse: false }));
    }
  }, [dispatch]);

  const handleFeatureTreeCheck = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, featureTree: true }));
    try {
      await dispatch(checkFeatureTreeGeneration());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, featureTree: false }));
    }
  }, [dispatch]);

  const handleKratosHealth = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, kratos: true }));
    try {
      await dispatch(checkKratosHealth());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, kratos: false }));
    }
  }, [dispatch]);

  const handleKratosAdminHealth = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, kratos_admin: true }));
    try {
      await dispatch(checkKratosAdminHealth());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, kratos_admin: false }));
    }
  }, [dispatch]);

  const handleKratosUsers = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, kratos: true }));
    try {
      await dispatch(checkKratosUsers());
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, kratos: false }));
    }
  }, [dispatch]);

  const handleKratosDetails = useCallback(() => {
    setKratosDetailsModal(true);
  }, []);

  const handleKratosDetailed = useCallback(async () => {
    setInfrastructureLoading((prev) => ({ ...prev, kratos: true }));
    try {
      const result = await dispatch(checkKratosDetailed());
      setKratosDetailed(result);
    } finally {
      setInfrastructureLoading((prev) => ({ ...prev, kratos: false }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      dispatch(performQuickHealthCheck());
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, dispatch]);

  const { healthyServicesCount, totalServicesCount, accessibleServices } = useMemo(() => {
    const totalServicesCount = DIRECT_SERVICES.length;
    const healthyServicesCount = Object.values(networkDiagnostics || {}).filter(
      (d) => d && d.status === 'healthy'
    ).length;
    const accessibleServices = isAuthenticated
      ? totalServicesCount
      : DIRECT_SERVICES.filter((s) => !s.requiresAuth).length;

    return { healthyServicesCount, totalServicesCount, accessibleServices };
  }, [networkDiagnostics, isAuthenticated]);

  const authRequiredServices = useMemo(
    () => DIRECT_SERVICES.filter((s) => s.requiresAuth).length,
    []
  );

  const rabbitmqQueuesCount = rabbitmqStatus?.metrics?.queues || 0;
  const rabbitmqConnectionsCount = rabbitmqStatus?.metrics?.connections || 0;
  const rabbitmqMessagesCount = rabbitmqStatus?.metrics?.messages || 0;

  const kratosUsersCount = kratosStatus?.users?.total_users || 0;
  const kratosActiveUsers = kratosStatus?.users?.active_users || 0;

  const featureTreeNodesCount = featureTreeStatus?.metrics?.totalNodes || 0;
  const featureTreeVersion = featureTreeStatus?.metrics?.version || 'unknown';

  const isSendRequestDisabled = !selectedService || !restPath || restLoading;
  const selectedServiceRequiresAuth =
    selectedService && DIRECT_SERVICES.find((s) => s.key === selectedService)?.requiresAuth;

  const isValidJson = (str) => {
    if (!str) return true;
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const headerProps = {
    isAuthenticated,
    user,
    healthyServicesCount,
    totalServicesCount,
    accessibleServices,
    authRequiredServices,
    networkDiagnostics,
    rabbitmqStatus,
    tarantoolStatus,
    mongodbStatus,
    featureTreeStatus,
    kratosStatus,
    infrastructureLoading,
    healthCheckLoading,
    rabbitmqQueuesCount,
    rabbitmqConnectionsCount,
    rabbitmqMessagesCount,
    kratosUsersCount,
    kratosActiveUsers,
    featureTreeNodesCount,
    featureTreeVersion,
    handleRabbitMQDetails,
    handleKratosDetails,
    authLoading,
    handleLogin,
    handleCheckAuth,
    handleLogout,
  };

  const actionsProps = {
    healthCheckLoading,
    authLoading,
    isAuthenticated,
    handleQuickHealthCheck,
    handleCompleteHealthCheck,
    handleInfrastructureCheck,
    handleLogin,
    handleCheckAuth,
    handleLogout,
    setRoutesModal,
    autoRefresh,
    setAutoRefresh,
    healthyServicesCount,
    accessibleServices,
    totalServicesCount,
    authRequiredServices,
    rabbitmqStatus,
    rabbitmqQueuesCount,
    rabbitmqConnectionsCount,
    rabbitmqMessagesCount,
    kratosStatus,
    kratosUsersCount,
    kratosActiveUsers,
    featureTreeStatus,
    featureTreeNodesCount,
    featureTreeVersion,
  };

  const contentProps = {
    activeTab,
    isAuthenticated,
    user,
    networkDiagnostics,
    healthCheckLoading,
    rabbitmqStatus,
    tarantoolStatus,
    mongodbStatus,
    clickhouseStatus,
    featureTreeStatus,
    kratosStatus,
    selectedService,
    restPath,
    restMethod,
    restBody,
    restResponse,
    restLoading,
    infrastructureLoading,
    logs,
    autoScroll,
    logFilter,
    logExpanded,
    logSearch,
    logLevels,
    logAreaRef,
    handleCheckAuth,
    handleServiceTest,
    handleSendRestRequest,
    handleSendCustomRequest,
    handleClearLogs,
    handleExportLogs,
    scrollToTop,
    scrollToBottom,
    handleRabbitMQHealth,
    handleRabbitMQDetails,
    handleRabbitMQTest,
    handleKratosHealth,
    handleKratosAdminHealth,
    handleKratosUsers,
    handleKratosDetails,
    handleKratosDetailed,
    handleFeatureTreeCheck,
    handleTarantoolHealth,
    handleMongoDBHealth,
    handleClickHouseHealth,
    handleClickHouseDetailed,
    handleTarantoolDetailed,
    handleMongoDBDetailed,
    setActiveTab: (tab) => dispatch(setActiveTab(tab)),
    setSelectedService: (service) => dispatch(setSelectedService(service)),
    setRestPath: (path) => dispatch(setRestPath(path)),
    setRestMethod: (method) => dispatch(setRestMethod(method)),
    setRestBody: (body) => dispatch(setRestBody(body)),
    setAutoScroll,
    setLogFilter,
    setLogExpanded,
    setLogSearch,
    setLogLevels,
    isSendRequestDisabled,
    selectedServiceRequiresAuth,
    isValidJson,
    imageLangEncodeResult,
    imageLangDecodeResult,
    handleImageLangEncode,
    handleImageLangDecode,
    dispatch,
  };

  const modalProps = {
    serviceTestModal,
    setServiceTestModal,
    routesModal,
    setRoutesModal,
    rabbitmqDetailsModal,
    setRabbitmqDetailsModal,
    kratosDetailsModal,
    setKratosDetailsModal,
    rabbitmqStatus,
    kratosStatus,
    infrastructureLoading,
    isAuthenticated,
    user,
    handleRefreshRabbitMQDetails,
    handleKratosHealth,
    handleKratosAdminHealth,
    handleKratosUsers,
    handleKratosDetailed,
    handleSendCustomRequest,
  };

  return (
    <PageShell>
      <ServicesTesterHeader {...headerProps} />
      <ServicesTesterActions {...actionsProps} />
      <ServicesTesterContent {...contentProps} />
      <Modals {...modalProps} />
    </PageShell>
  );
}

function Modals({
  serviceTestModal,
  setServiceTestModal,
  routesModal,
  setRoutesModal,
  rabbitmqDetailsModal,
  setRabbitmqDetailsModal,
  kratosDetailsModal,
  setKratosDetailsModal,
  rabbitmqStatus,
  kratosStatus,
  infrastructureLoading,
  isAuthenticated,
  user,
  handleRefreshRabbitMQDetails,
  handleKratosHealth,
  handleKratosAdminHealth,
  handleKratosUsers,
  handleKratosDetailed,
  handleSendCustomRequest,
}) {
  return (
    <>
      <ServiceTestModal
        open={serviceTestModal.open}
        onClose={() => setServiceTestModal({ open: false, service: null })}
        service={serviceTestModal.service}
        onSendRequest={handleSendCustomRequest}
        isAuthenticated={isAuthenticated}
      />

      <RoutesModal open={routesModal} onClose={() => setRoutesModal(false)} />

      <RabbitMQDetailsModal
        open={rabbitmqDetailsModal}
        onClose={() => setRabbitmqDetailsModal(false)}
        rabbitmqStatus={rabbitmqStatus}
        onRefresh={handleRefreshRabbitMQDetails}
        loading={infrastructureLoading.rabbitmq}
      />

      <KratosDetailsModal
        open={kratosDetailsModal}
        onClose={() => setKratosDetailsModal(false)}
        kratosStatus={kratosStatus}
        onRefresh={handleKratosHealth}
        onCheckAdmin={handleKratosAdminHealth}
        onCheckUsers={handleKratosUsers}
        onCheckDetailed={handleKratosDetailed}
        loading={infrastructureLoading.kratos}
        adminLoading={infrastructureLoading.kratos_admin}
        isAuthenticated={isAuthenticated}
        user={user}
      />
    </>
  );
}
